/**
 * Production Floor View
 *
 * Simplified interface for production workers.
 * Shows jobs for selected work center with routing context and time tracking.
 *
 * UX Principles:
 * - One primary action per view (START/PAUSE/COMPLETE)
 * - Design for distracted workers - large touch targets
 * - Eliminate cognitive load - show only what's needed
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import ProductionJobQueue from './ProductionJobQueue';
import ProductionActionBar from './ProductionActionBar';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Work centers available for production workers
const WORK_CENTERS = [
  { id: 'PA', label: 'Production Art' },
  { id: 'OPTI', label: 'OPTI/Finishing' },
  { id: 'ASSEMBLY', label: 'Assembly' },
  { id: 'PAINT', label: 'Paint' },
  { id: '3D MODEL', label: '3D Model' },
];

export default function ProductionFloorView({ onBackToApp, userEmail }) {
  // Work center selection (persisted)
  const [selectedWorkCenter, setSelectedWorkCenter] = useState(() => {
    return localStorage.getItem('3dcolor-floor-workcenter') || 'PA';
  });

  // Jobs for current work center
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selected job for action
  const [selectedJob, setSelectedJob] = useState(null);

  // Active sessions from API
  const [activeSessions, setActiveSessions] = useState({});

  // Timer tick for live updates
  const [tick, setTick] = useState(0);

  // Auto-refresh
  const [lastRefresh, setLastRefresh] = useState(null);

  const getToken = () => sessionStorage.getItem('msal.backendToken');

  // Persist work center selection
  useEffect(() => {
    localStorage.setItem('3dcolor-floor-workcenter', selectedWorkCenter);
  }, [selectedWorkCenter]);

  // Timer tick for live updates
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch jobs for work center
  const fetchJobs = useCallback(async () => {
    const token = getToken();
    console.log('🏭 Floor View: Fetching jobs for', selectedWorkCenter, 'token:', token ? 'present' : 'MISSING');

    if (!token) {
      console.error('🏭 Floor View: No auth token!');
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🏭 Floor View: Making request to /api/workcenter/schedule');

      // Fetch schedule data (same as WorkCenterSchedule)
      const res = await fetch(`${API_BASE}/api/workcenter/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Failed to load: ${res.status}`);

      const data = await res.json();
      console.log('🏭 Floor View: Response received', data);
      console.log('🏭 Floor View: Work centers available:', Object.keys(data.workCenters || {}));

      const wcData = data.workCenters?.[selectedWorkCenter];
      console.log('🏭 Floor View: Data for', selectedWorkCenter, ':', wcData?.jobs?.length || 0, 'jobs');

      // Transform jobs to include routing context
      // API returns pendingOperations, not operations
      const jobsWithRouting = (wcData?.jobs || []).map(job => ({
        ...job,
        // Map pendingOperations to operations for consistency
        operations: job.pendingOperations || [],
        // Find current operation for this work center
        currentOperation: (job.pendingOperations || [])[0], // First pending op is current
      }));

      console.log(`📋 Floor View: ${jobsWithRouting.length} jobs for ${selectedWorkCenter}`);
      setJobs(jobsWithRouting);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkCenter]);

  // Fetch active sessions
  const fetchActiveSessions = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/me/time/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        // API returns { tickets: [...] } with active time tickets
        const sessionsMap = {};
        (data.tickets || []).forEach(ticket => {
          sessionsMap[ticket.jobId] = {
            ...ticket,
            state: ticket.state || 'running',
            startTime: ticket.startTime,
            accumulatedSeconds: ticket.accumulatedSeconds || 0,
          };
        });
        setActiveSessions(sessionsMap);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  }, []);

  // Initial fetch and refresh interval
  useEffect(() => {
    fetchJobs();
    fetchActiveSessions();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchJobs();
      fetchActiveSessions();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchJobs, fetchActiveSessions]);

  // Time tracking actions
  const handleStartJob = async (job, operationCode) => {
    const token = getToken();
    if (!token || !job) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${job.jobId}/time/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operationCode,
          workCenter: selectedWorkCenter,
          orderNumber: job.orderNumber,
          quoteNumber: job.quoteNumber,
        }),
      });

      if (!res.ok) throw new Error('Failed to start');

      await fetchActiveSessions();
      setSelectedJob(job);
    } catch (err) {
      console.error('Error starting job:', err);
      alert('Failed to start job');
    }
  };

  const handlePauseJob = async (jobId) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/time/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to pause');
      await fetchActiveSessions();
    } catch (err) {
      console.error('Error pausing job:', err);
    }
  };

  const handleResumeJob = async (jobId) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/time/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to resume');
      await fetchActiveSessions();
    } catch (err) {
      console.error('Error resuming job:', err);
    }
  };

  const handleCompleteJob = async (jobId, piecesFinished = 0) => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/time/complete`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ piecesFinished }),
      });

      if (!res.ok) throw new Error('Failed to complete');

      await fetchActiveSessions();
      await fetchJobs();
      setSelectedJob(null);
    } catch (err) {
      console.error('Error completing job:', err);
    }
  };

  // Format elapsed time for display
  const formatElapsed = useCallback((session) => {
    if (!session) return '0:00';

    const accumulated = Math.floor(session.accumulatedSeconds || 0);
    let seconds = 0;

    if (session.state === 'running' && session.startTime) {
      const startMs = new Date(session.startTime).getTime();
      const currentSegment = Math.floor((Date.now() - startMs) / 1000);
      seconds = accumulated + currentSegment;
    } else if (session.durationSeconds) {
      seconds = Math.floor(session.durationSeconds);
    } else {
      seconds = accumulated;
    }

    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  }, []);

  // Currently active job (if any)
  const activeJob = useMemo(() => {
    const activeSession = Object.entries(activeSessions).find(
      ([_, s]) => s?.state === 'running' || s?.state === 'paused'
    );
    if (!activeSession) return null;

    const [jobId, session] = activeSession;
    const job = jobs.find(j => j.jobId === jobId);
    return job ? { ...job, session } : { jobId, session };
  }, [activeSessions, jobs]);

  // Force re-render on tick
  void tick;

  return (
    <div className="flex-1 overflow-auto bg-white text-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
        {/* Work Center Selector */}
        <div className="px-4 pb-3">
          <div className="flex gap-2 overflow-x-auto">
            {WORK_CENTERS.map(wc => (
              <button
                key={wc.id}
                onClick={() => setSelectedWorkCenter(wc.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedWorkCenter === wc.id
                    ? 'bg-white text-indigo-700'
                    : 'bg-slate-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                {wc.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pb-24">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : error ? (
          <div className="p-4 m-4 bg-red-100 border border-red-700 rounded-lg">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchJobs}
              className="mt-2 px-3 py-1 bg-red-700 rounded text-sm"
            >
              Retry
            </button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-lg">No jobs for {selectedWorkCenter}</p>
            <p className="text-sm mt-1">All caught up!</p>
          </div>
        ) : (
          <ProductionJobQueue
            jobs={jobs}
            activeSessions={activeSessions}
            selectedJob={selectedJob}
            onSelectJob={setSelectedJob}
            formatElapsed={formatElapsed}
            workCenter={selectedWorkCenter}
          />
        )}
      </main>

      {/* Action Bar */}
      <ProductionActionBar
        activeJob={activeJob}
        selectedJob={selectedJob}
        activeSessions={activeSessions}
        onStart={handleStartJob}
        onPause={handlePauseJob}
        onResume={handleResumeJob}
        onComplete={handleCompleteJob}
        formatElapsed={formatElapsed}
        workCenter={selectedWorkCenter}
      />
    </div>
  );
}
